import { useForm } from '@mantine/form';
import { useContext, useEffect, useState } from 'react'
import { AddKFHRmRequest, BaseResult, CustomClaims, NotificationMSG, RmAuthResult, RMUser } from '../../types';
import { HttpsCallableResult, httpsCallable } from 'firebase/functions';
import { Accordion, Alert, Button, Checkbox, Group, Paper, Select, Space, Text, TextInput } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { FirebaseError } from 'firebase/app';

import { ConfigContext, MyUserDataContext } from '../../contexts';

import PasswordStrengthComponent, { passwordRequirements } from './PasswordStrengthComponent';
import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import { RmsSelectComponent } from '../../components/rms_select_component';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { History } from '../../components/history';
import MobileCodeSelect, { describeInvalidMobile, dialCodeForSegment, isValidLocalMobile, lookupMobile, splitMobileWithDialCode } from '../../components/mobile_code_select';

interface AddRmFormType {
    rmUser?: RMUser | null
    handleSubmit: () => void
}
const AddRmForm = ({ rmUser, handleSubmit }: AddRmFormType) => {
    const userContext = useContext(MyUserDataContext);
    const configContext = useContext(ConfigContext);
    let userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};

    const functions = useFunctions();
    const [msg, setMsg] = useState<NotificationMSG>();
    const [loading, setLoading] = useState<boolean>(false)
    const [orderHandler, setOrderHandler] = useState<RMUser | undefined>();
    const [liveRm, setLiveRm] = useState<RMUser | undefined>(rmUser ?? undefined);
    const db = useFirestore();


    // useEffect(() => {
    //     if (rmUser) {
    //         setLoading(true);
    //         const getAndSyncRmAuthData = httpsCallable<string, RmAuthResult>(functions, "getAndSyncRmAuthData");
    //         getAndSyncRmAuthData(rmUser?.email)
    //             .then((result) => {
    //                 form.setFieldValue("active", result.data.active ?? rmUser.active);
    //                 form.setFieldValue("twoFactorEnabled", result.data.twoFactorEnabled ?? rmUser.twoFactorEnabled);
    //             })
    //             .finally(() => {
    //                 console.log("Done from sync");
    //                 setLoading(false)
    //             });
    //     }

    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [])

    //get RMs
    useEffect(() => {

        if (!rmUser?.order_handler_email) {
            return;
        }

        const rmDoc = doc(db, "rms", rmUser?.order_handler_email.toLowerCase());
        // let q = query(rmCollection, where("active", "==", true));

        //is it a KFH RM or operator?  If so, limit it to the segment
        // if (userClaims["kfh_rm"] || userClaims["kfh_oper"]) {
        //   q = query(q, where("segment", "==", userClaims.segment));  //TODO: we need the RM segment asap!
        // }


        return onSnapshot(rmDoc, (snapshot) => {
            if (snapshot.metadata.fromCache) {
                console.log("RM Cached data", snapshot.exists());
                // return;
            } else {
                console.log("RM Server data", snapshot.exists());
            }

            // let dict = {} as any;
            let rm: RMUser | undefined = undefined;

            if (snapshot.exists()) {
                rm = snapshot.data() as RMUser;
            }



            setOrderHandler(rm);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rmUser?.order_handler_email]);



    // RM numbers are stored as local digits + mobile_code. Legacy RM docs may have the
    // dial code baked into `mobile` ("+2010..."), so split it back out for the form.
    const initialSegment = rmUser ? rmUser?.segment : userClaims.segment;
    const initialRmMobile = splitMobileWithDialCode(rmUser?.mobile || "", rmUser?.mobile_code || dialCodeForSegment(initialSegment));

    const form = useForm<AddKFHRmRequest>({
        initialValues: {
            email: rmUser?.email?.toLowerCase() || "",
            emailVerified: true,
            phoneNumber: initialRmMobile.local,
            mobile: initialRmMobile.local,
            mobile_code: initialRmMobile.dialCode,
            displayName: rmUser?.name_en || "",
            disabled: false,
            name_ar: rmUser?.name_ar || "",
            name_en: rmUser?.name_en || "",
            segment: rmUser ? rmUser?.segment : (userClaims.segment),
            role: rmUser?.role || "rm",
            rm_id: rmUser?.rm_id,
            active: rmUser?.active,
            twoFactorEnabled: rmUser?.twoFactorEnabled,
            certified: rmUser?.certified,
            order_handler_email: rmUser?.order_handler_email,

        },

        validate: {
            email: (val) => (/^\S+@\S+$/.test(val) ? null : 'Invalid email'),
            password: (val?: string) => {
                let err: string | null = null

                if (!rmUser) {
                    if (!val)
                        err = "Please enter a valid password"
                    if (val?.length! <= 7)
                        err = 'Password should include at least 8 characters'


                    passwordRequirements.forEach((requirement: any) => {
                        if (!requirement.re.test(val)) {
                            err = requirement.label as (string)
                        }
                    });
                }
                return err



            },
            phoneNumber: (val, values) => {
                const dialCode = values.mobile_code || dialCodeForSegment(values.segment);
                const parts = splitMobileWithDialCode(val || "", dialCode);
                if (!parts.local) return 'Phone number is required';
                // shape check only; Twilio Lookup validates the number on submit
                if (isValidLocalMobile(parts.local, parts.dialCode)) return null;
                return `Enter the local number digits only (country code ${parts.dialCode} is selected separately)`;
            },
        },
    });

    //Subscribe to the RM's own Firestore doc when editing so the form always reflects the
    //source of truth immediately after a save, instead of the (possibly stale) Typesense hit
    //passed in via `rmUser`. Typesense reindexing is async, so reopening the edit modal right
    //after saving would otherwise show old values.
    useEffect(() => {
        if (!rmUser?.email) {
            return;
        }

        const rmDocRef = doc(db, "rms", rmUser.email.toLowerCase());

        return onSnapshot(rmDocRef, (snapshot) => {
            if (!snapshot.exists()) {
                return;
            }

            const rm = snapshot.data() as RMUser;
            setLiveRm(rm);

            const liveMobile = splitMobileWithDialCode(rm.mobile ?? "", rm.mobile_code || dialCodeForSegment(rm.segment));
            form.setValues({
                name_en: rm.name_en ?? "",
                displayName: rm.name_en ?? "",
                name_ar: rm.name_ar ?? "",
                mobile: liveMobile.local,
                phoneNumber: liveMobile.local,
                mobile_code: liveMobile.dialCode,
                rm_id: rm.rm_id,
                segment: rm.segment,
                role: rm.role ?? "rm",
                active: rm.active,
                twoFactorEnabled: rm.twoFactorEnabled,
                certified: rm.certified,
                order_handler_email: rm.order_handler_email,
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rmUser?.email]);

    return (
        <div>

            <form

                onSubmit={form.onSubmit(async (values) => {
                    // normalize to local digits + dial code before sending
                    const parts = splitMobileWithDialCode(values.phoneNumber || values.mobile || "", values.mobile_code || dialCodeForSegment(values.segment));
                    const formValues: AddKFHRmRequest = { ...values, phoneNumber: parts.local, mobile: parts.local, mobile_code: parts.dialCode };
                    try {
                        setLoading(true)
                        // Twilio Lookup validates the number for any country; the backend repeats this check.
                        const lookup = await lookupMobile(functions, parts.local, parts.dialCode);
                        if (lookup.valid === false) {
                            form.setFieldError("phoneNumber", describeInvalidMobile(lookup));
                            setLoading(false);
                            return;
                        }
                        let res: HttpsCallableResult<BaseResult>;
                        if (rmUser) {
                            const updateRM = httpsCallable<AddKFHRmRequest, BaseResult>(functions, "updateRM");
                            res = await updateRM(formValues);

                        }
                        else {

                            const addKFHRm = httpsCallable<AddKFHRmRequest, BaseResult>(functions, "addKFHRm");
                            res = await addKFHRm(formValues);

                        }
                        if (res.data.status === "success") {
                            setMsg({ type: "success", message: "Success" });
                            setLoading(false)

                            setTimeout(() => {
                                if (handleSubmit) {
                                    handleSubmit()
                                }
                            }

                                , 500);
                        }
                        else {
                            if (res?.data.message) {
                                setMsg({ type: "error", message: res.data.message });

                            }
                            else {

                                setMsg({ type: "error", message: "Something went wrong..." });
                            }
                        }
                    }
                    catch (e: any) {

                        if (e instanceof FirebaseError) {
                            setMsg({ type: "error", message: e.message });

                        }
                        else {
                            setMsg({ type: "error", message: "Something went wrong..." });

                        }
                    }

                    setLoading(false)



                })}
            >
                <TextInput
                    {...form.getInputProps("email")}
                    required
                    disabled={!!rmUser}
                    onChange={(event) => {
                        form.setFieldValue("email", event.target.value.toLowerCase())
                    }}
                    label="Email" />

                <TextInput
                    {...form.getInputProps("name_en")}
                    required
                    onChange={(event) => {
                        form.setFieldValue("name_en", event.target.value)
                        form.setFieldValue("displayName", event.target.value)
                    }}
                    label="Name (English)" />

                <TextInput
                    {...form.getInputProps("name_ar")}
                    required
                    label="Name (Arabic)" />

                <TextInput
                    {...form.getInputProps("rm_id")}
                    required
                    label="ID" />

                <Group align="start" wrap="nowrap" gap="xs">
                    <MobileCodeSelect
                        style={{ flex: "0 0 45%" }}
                        value={form.values.mobile_code || dialCodeForSegment(form.values.segment)}
                        onChange={(dialCode) => form.setFieldValue("mobile_code", dialCode)}
                    />
                    <TextInput
                        style={{ flex: 1 }}
                        {...form.getInputProps("phoneNumber")}
                        required
                        onChange={(event) => {
                            const typed = event.target.value;
                            // a full "+20..." number typed here moves its country code into the picker
                            const parts = splitMobileWithDialCode(typed, form.values.mobile_code || dialCodeForSegment(form.values.segment));
                            const local = parts.matched ? parts.local : typed;
                            if (parts.matched) form.setFieldValue("mobile_code", parts.dialCode);
                            form.setFieldValue("phoneNumber", local)
                            form.setFieldValue("mobile", local)
                        }}
                        label="Phone Number" />
                </Group>
                <Text size="xs" c="dimmed">
                    Used for the login OTP and WhatsApp notifications. KFH Egypt RMs default to +20; all other segments default to +965 (Kuwait).
                </Text>

                <Select
                    {...form.getInputProps("segment")}
                    label="Segment"
                    required
                    disabled={userClaims.admin != true}
                    data={configContext?.segments?.segmentList?.map((segment) => {
                        return segment.name;
                    })}
                    onChange={(value) => {
                        const previousDefault = dialCodeForSegment(form.values.segment);
                        form.setFieldValue("segment", value ?? undefined);
                        // follow the segment default unless the admin already picked a specific country code
                        if (!form.values.mobile_code || form.values.mobile_code === previousDefault) {
                            form.setFieldValue("mobile_code", dialCodeForSegment(value));
                        }
                    }}
                />
                <Checkbox
                    mt={"xl"}
                    {...form.getInputProps("certified")}
                    checked={form.values.certified}
                    disabled={loading}
                    onChange={(event) => {
                        form.setFieldValue("certified", event?.currentTarget.checked);
                        if (!event?.currentTarget.checked) {
                            form.setFieldValue("order_handler_email", "");
                        }

                    }}
                    label="CMA Licensed" />
                {/* NEED TO ADD DEFAULT VALUE BY EMAIL */}
                {!form.values.certified && <RmsSelectComponent

                    required={false}
                    defaultValue={orderHandler?.name_en}
                    textProps={{
                        label: "Orders Handler:",
                        placeholder: "search RM by name or email",
                        disabled: loading || form.values.certified

                    }}
                    handleSubmit={(selectedRM: RMUser) => {
                        if (!selectedRM.certified) {
                            setMsg({
                                type: "error",
                                message: "Handler need to be certified"
                            });
                            form.setFieldValue("order_handler_email", "");
                        } else {
                            setMsg(undefined);
                            form.setFieldValue("order_handler_email", selectedRM.email);
                        }

                    }}

                />}

                {!rmUser &&
                    <PasswordStrengthComponent passwordValue={form.values.password || ""} passwordProps={form.getInputProps("password")} />

                }

                {rmUser &&
                    <>
                        <Checkbox
                            mt={"xl"}
                            {...form.getInputProps("active")}
                            checked={form.values.active}
                            disabled={loading}

                            label="Active" />

                        <Checkbox
                            mt={"xl"}
                            {...form.getInputProps("twoFactorEnabled")}
                            checked={form.values.twoFactorEnabled}
                            disabled={loading}

                            label="Use Two-Factor Authentication" />

                    </>
                }
                <Space h="md" />
                {msg && (
                    <Alert
                        icon={<IconAlertCircle size={16} />}
                        color={msg.type === "success" ? "green" : "red"}
                        title={msg.type === "success" ? "Success" : "Failure"}
                    >
                        {msg.message}
                    </Alert>
                )}
                <Space h="md" />

                <Group justify="center" style={{ width: "100%" }}>
                    <Button type='submit' loading={loading}>
                        {rmUser ? "Update" : "Create"} Rm
                    </Button>
                </Group>

            </form>

            {rmUser && (
                <>
                    <Space h="xl" />
                    {liveRm?.lastOperation && (
                        <Paper withBorder p="sm" mb="md" radius="md">
                            <Text size="sm" fw={600}>Last update</Text>
                            <Text size="sm">
                                {liveRm.lastOperation.msg}
                                {liveRm.lastOperation.by ? ` by ${liveRm.lastOperation.by}` : ""}
                                {liveRm.lastOperation.date?.toDate
                                    ? ` on ${liveRm.lastOperation.date.toDate().toLocaleString("en-GB", { hour12: true })}`
                                    : ""}
                            </Text>
                            {liveRm.lastOperation.details && (
                                <Text size="xs" c="dimmed">{liveRm.lastOperation.details}</Text>
                            )}
                        </Paper>
                    )}
                    <Accordion variant="contained">
                        <Accordion.Item value="history">
                            <Accordion.Control>History</Accordion.Control>
                            <Accordion.Panel>
                                <History historyCollection={collection(db, "rms", rmUser.email.toLowerCase(), "history")} defaultRowsPerPage={25} />
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                </>
            )}

        </div >
    )
}

export default AddRmForm
